import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdSearch, MdClose, MdHome } from "react-icons/md";

const MAX_SUGGESTIONS = 7;

// wraps the matched part of a name in <mark>
function Highlight({ name, query }) {
	const start = name.toLowerCase().indexOf(query.toLowerCase());
	if (!query || start < 0) return name;
	const end = start + query.length;
	return (
		<>
			{name.slice(0, start)}
			<mark>{name.slice(start, end)}</mark>
			{name.slice(end)}
		</>
	);
}

export default function Searchbox(props) {
	const { data = [], type } = props;
	const navigate = useNavigate();
	const location = useLocation();
	const wrapperRef = useRef(null);
	const [text, setText] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const [isSearching, setIsSearching] = useState(false);
	const [notFound, setNotFound] = useState(false);

	// detail routes live under the current section, e.g. /hostels/:slug or /obuasi-campus/:slug
	const basePath = `/${location.pathname.split("/")[1]}`;
	const query = text.trim();

	const suggestions = useMemo(() => {
		if (!query) return [];
		const q = query.toLowerCase();
		return (data || [])
			.filter((item) => item?.name?.toLowerCase().includes(q))
			.slice(0, MAX_SUGGESTIONS);
	}, [data, query]);

	// close the dropdown when clicking outside the search bar
	useEffect(() => {
		const handleClick = (e) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target))
				setIsOpen(false);
		};
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, []);

	const onChangeHandler = (value) => {
		setText(value);
		setActiveIndex(-1);
		setNotFound(false);
		setIsOpen(value.trim().length > 0);
	};

	const onSuggestion = (item) => {
		setText(item.name);
		setIsOpen(false);
		navigate(`${basePath}/${item.slug}`);
	};

	const handleSearch = async () => {
		if (!query) return;
		if (activeIndex >= 0 && suggestions[activeIndex])
			return onSuggestion(suggestions[activeIndex]);
		if (suggestions.length > 0) return onSuggestion(suggestions[0]);

		// nothing matched locally, ask the server
		setIsSearching(true);
		try {
			const res = await axios({
				method: "post",
				url: `${process.env.REACT_APP_API_URL}/api/v1/residences/search`,
				headers: { "Content-Type": "application/json" },
				data: JSON.stringify({ search: query }),
			});
			const match = res.data?.data?.[0];
			if (match?.slug) {
				setIsOpen(false);
				navigate(`${basePath}/${match.slug}`);
			} else {
				setNotFound(true);
				setIsOpen(true);
			}
		} catch (err) {
			console.log(err);
			setNotFound(true);
			setIsOpen(true);
		} finally {
			setIsSearching(false);
		}
	};

	const onKeyDown = (e) => {
		if (e.key === "ArrowDown" && suggestions.length) {
			e.preventDefault();
			setIsOpen(true);
			setActiveIndex((i) => (i + 1) % suggestions.length);
		} else if (e.key === "ArrowUp" && suggestions.length) {
			e.preventDefault();
			setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
		} else if (e.key === "Escape") {
			setIsOpen(false);
			setActiveIndex(-1);
		}
	};

	const clear = () => {
		setText("");
		setIsOpen(false);
		setNotFound(false);
		setActiveIndex(-1);
	};

	const showDropdown = isOpen && query.length > 0;

	return (
		<div className="sb mt-5" ref={wrapperRef}>
			<form
				className="sb-field"
				role="search"
				onSubmit={(e) => {
					e.preventDefault();
					handleSearch();
				}}
			>
				<MdSearch className="sb-lead-icon" aria-hidden="true" />
				<input
					type="search"
					className="sb-input"
					value={text}
					onChange={(e) => onChangeHandler(e.target.value)}
					onFocus={() => query && setIsOpen(true)}
					onKeyDown={onKeyDown}
					placeholder={`Search ${type} by Name`}
					aria-label={`Search ${type} by name`}
					role="combobox"
					aria-expanded={showDropdown}
					aria-controls="sb-listbox"
					aria-autocomplete="list"
					aria-activedescendant={
						activeIndex >= 0 ? `sb-option-${activeIndex}` : undefined
					}
					autoComplete="off"
				/>
				{text && (
					<button
						type="button"
						className="sb-clear"
						onClick={clear}
						aria-label="Clear search"
					>
						<MdClose />
					</button>
				)}
				<button type="submit" className="sb-submit" disabled={isSearching}>
					{isSearching ? (
						<span className="sb-spinner" aria-hidden="true" />
					) : (
						<MdSearch className="sb-submit-icon" aria-hidden="true" />
					)}
					<span className="sb-submit-text">Search</span>
				</button>
			</form>

			{showDropdown && (
				<div className="sb-dropdown">
					{suggestions.length > 0 ? (
						<ul id="sb-listbox" role="listbox" className="sb-list">
							{suggestions.map((item, index) => (
								<li
									key={item?._id || item?.slug}
									id={`sb-option-${index}`}
									role="option"
									aria-selected={index === activeIndex}
									className={`sb-option${
										index === activeIndex ? " is-active" : ""
									}`}
									onMouseEnter={() => setActiveIndex(index)}
									// mousedown fires before the input loses focus
									onMouseDown={(e) => {
										e.preventDefault();
										onSuggestion(item);
									}}
								>
									<MdHome className="sb-option-icon" aria-hidden="true" />
									<span className="sb-option-text">
										<Highlight name={item.name} query={query} />
									</span>
								</li>
							))}
						</ul>
					) : (
						<p className="sb-empty">
							{notFound
								? `No ${type.toLowerCase()} found for "${query}"`
								: `Press Enter to search for "${query}"`}
						</p>
					)}
				</div>
			)}
		</div>
	);
}
